import { Alert, Button, Divider, MenuItem, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { LoadingButton } from 'src/components/atoms/loading-button/loading-button';
import { useKeyDown } from 'src/hooks/use-key-down';
import { Logo } from 'src/icons/logo';
import { SSOProvider } from '../../../../../auth/supabase-provider-registry';
import { SSOProviderStepFormValues } from '../../../schema/use-form-validation-schema';
import AuthFormWrapper from '../../templates/auth-form-wrapper';
import AuthFormHeader from '../auth-form-header';

interface Props {
  onSubmit: (values: SSOProviderStepFormValues) => Promise<void>;
  onBackButtonClick: () => void;
  ssoProviders: SSOProvider[];
}

export const SSOProvidersStep = ({
  onSubmit,
  onBackButtonClick,
  ssoProviders,
}: Props) => {
  const { control, handleSubmit } = useFormContext<SSOProviderStepFormValues>();
  const { isValid, isSubmitting, errors } =
    useFormState<SSOProviderStepFormValues>();

  const { t } = useTranslation();

  useKeyDown('Enter', handleSubmit(onSubmit));

  return (
    <AuthFormWrapper
      actionsSlot={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}>
          <Button variant="outlined" onClick={onBackButtonClick}>
            {t('common:button.back')}
          </Button>
          <LoadingButton
            variant="contained"
            size="medium"
            disabled={!isValid}
            loading={isSubmitting}
            onClick={handleSubmit(onSubmit)}>
            {t('auth:login.loginButton')}
          </LoadingButton>
        </Box>
      }>
      <AuthFormHeader
        title={t('auth:login.title')}
        description={t('auth:login.contentDepartments')}
        icon={<Logo />}
      />
      <Box>
        <Typography
          color="textSecondary"
          variant="body2"
          sx={{ fontSize: 12, textAlign: 'center' }}>
          {t('auth:login.departments.hintDepartments')}
        </Typography>
        <Divider sx={{ mt: 3, mb: 1 }}>
          <Typography
            color="textPrimary"
            variant="body2"
            sx={{ fontSize: 12, textAlign: 'center' }}>
            {t('auth:login.departments.subtitleDepartments')}
          </Typography>
        </Divider>
        <Controller
          name="ssoProviderId"
          control={control}
          render={({
            field: { onChange, onBlur, value, ref, name },
            fieldState: { error },
          }) => (
            <InputField
              fullWidth
              name={name}
              label={t('auth:login.departments.labelDepartment')}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
              required={true}
              inputRef={ref}
              select
              error={!!error}
              helperText={error?.message}
              sx={{ fontSize: 14, mt: 2 }}>
              {ssoProviders.map(ssoProvider => (
                <MenuItem key={ssoProvider.id} value={ssoProvider.id}>
                  {ssoProvider.label}
                </MenuItem>
              ))}
            </InputField>
          )}
        />
        {errors?.root?.message ? (
          <Alert severity="error">{errors?.root?.message}</Alert>
        ) : null}
      </Box>
    </AuthFormWrapper>
  );
};
